import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ArrowLeft,
  Users,
  User,
  Briefcase,
  Building,
  ChevronDown,
  ChevronRight,
  X,
  Sparkles,
  Loader2,
} from 'lucide-react';
import Button from '../Button';
import StepContainer from '../StepContainer';
import SegmentCard from './SegmentCard';
import CustomSegmentCard from './CustomSegmentCard';
import DynamicSegmentationPanel, { DynamicSegmentationPanelRef } from './DynamicSegmentationPanel';
import { useAudience } from '../../context/AudienceContext';
// @ts-ignore
import { Dialog, DialogHeader, DialogTitle } from '../ui/dialog.jsx';
// @ts-ignore
import { CustomDialogContent as DialogContent, CustomDialogClose } from '../ui/dialog-custom.jsx';
import { cn } from '../../lib/utils';
import segmentFiltersConfig from '../../config/segmentFilters.json';
import { Segmentation, segmentationService } from '../../services/segmentationService';

interface Segment {
  id: number;
  name: string;
  description: string;
  len: number;
  created_at: string;
  updated_at: string;
  filters?: Record<string, any>; // Segment-level filter configuration
  [key: string]: any; // For other attributes
}

interface DynamicSegmentationData {
  dynamic_segmentation_id: number;
  dynamic_segment_indices: number[];
  persona_ids: number[];
  segment_names?: string[];
}

interface UnifiedSegmentsSelectorProps {
  btnText?: string;
  audienceId: number;
  onBack: () => void;
  onNext: (
    selectedSegments: number[],
    personaFilters: Record<number, any>,
    dynamicData?: DynamicSegmentationData,
  ) => void;
  onEditStep?: () => void;
  enableDynamicSegmentation?: boolean;
}

// Define a Persona type
interface PersonaType {
  id: number;
  name: string;
  age?: number | string;
  gender?: string;
  four_word_description?: string;
  job_title?: string;
  company_name?: string;
  industry_l1?: string;
  sub_industry_l2?: string;
  function?: string;
  role?: string;
  income?: string;
  education?: string;
  location?: string;
  interests?: (string | object)[];
  goals?: (string | object)[];
  pain_points?: (string | object)[];
  behaviors?: (string | object)[];
  values?: (string | object)[];
  preferred_channels?: (string | object)[];
  purchasing_habits?: (string | object)[];
  customer_profile_insights?: {
    triggers_and_motivations?: (string | object)[];
    concerns_and_obstacles?: (string | object)[];
    decision_criteria?: (string | object)[];
    expected_outcomes?: (string | object)[];
    path_to_purchase?: (string | object)[];
  };
  created_at: string;
  updated_at: string;
  segment_id?: number;
  data?: any;
  [key: string]: any; // For any other fields
}

const API_URL = import.meta.env.VITE_API_URL || '';

const UnifiedSegmentsSelector: React.FC<UnifiedSegmentsSelectorProps> = ({
  btnText = 'Continue to Use Cases',
  audienceId,
  onBack,
  onNext,
  onEditStep,
  enableDynamicSegmentation = true,
}) => {
  const { audienceData } = useAudience();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dynamic segmentation state - default to static (pre-built segments)
  const [activeTab, setActiveTab] = useState<'static' | 'dynamic'>('static');
  const [selectedDynamicSegmentation, setSelectedDynamicSegmentation] =
    useState<Segmentation | null>(null);
  const [selectedDynamicIndices, setSelectedDynamicIndices] = useState<number[]>([]);
  const [selectedDynamicPersonaIds, setSelectedDynamicPersonaIds] = useState<number[]>([]);
  const [isDynamicProcessing, setIsDynamicProcessing] = useState(false);

  // Custom segmentations shown in static view
  const [customSegmentations, setCustomSegmentations] = useState<Segmentation[]>([]);
  // Track selected indices per custom segmentation: { segmentationId: [selectedIndices] }
  const [customSegmentSelections, setCustomSegmentSelections] = useState<Record<number, number[]>>(
    {},
  );
  // Active custom segmentation being viewed (when set, only show these segments)
  const [activeCustomSegmentation, setActiveCustomSegmentation] = useState<Segmentation | null>(
    null,
  );

  // Filter and demographics state
  const [personaFilters, setPersonaFilters] = useState<Record<number, any>>({});
  const [segmentPersonaCounts, setSegmentPersonaCounts] = useState<Record<number, number>>({});
  const [loadingCounts, setLoadingCounts] = useState<Record<number, boolean>>({});
  const [segmentDemographics, setSegmentDemographics] = useState<Record<number, any>>({});
  const [loadingDemographics, setLoadingDemographics] = useState<Record<number, boolean>>({});
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  // Persona modal state
  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);
  const [selectedSegmentForPersonas, setSelectedSegmentForPersonas] = useState<number | null>(null);
  // True segment size when the persona preview list is capped, so the modal can say
  // "showing N of M" instead of letting the cap read as the segment's real size.
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<PersonaType | null>(null);
  const [expandedRoles, setExpandedRoles] = useState<Record<string, boolean>>({});
  // rolePersonaMap now stores full persona objects (with bio, demographic, sidebar fields)
  const [rolePersonaMap, setRolePersonaMap] = useState<Record<string, PersonaType[]>>({});
  const [loadingPersonaIds, setLoadingPersonaIds] = useState(false);

  // Tooltip state
  const [showInfoTooltip, setShowInfoTooltip] = useState<{
    segmentId: number | null;
    x: number;
    y: number;
  }>({
    segmentId: null,
    x: 0,
    y: 0,
  });

  // Add debounce refs for filter updates
  const filterUpdateTimeouts = useRef<{ [key: number]: ReturnType<typeof setTimeout> }>({});
  const pendingFilterUpdates = useRef<{ [key: number]: any }>({});

  // Ref for dynamic segmentation panel
  const dynamicPanelRef = useRef<DynamicSegmentationPanelRef>(null);

  // Centralized filter transformation helper - ensures consistency across all filter operations
  const transformFiltersForBackend = (
    filters: any,
    audienceName: string,
    segmentFilters?: Record<string, any>,
  ) => {
    // Dynamic filter keys that come from segment.filters (database)
    // These should be passed through as-is since they match persona data structure directly
    const dynamicFilterKeys = [
      'age_range',
      'income_bracket',
      'education_level',
      'location_type',
      'subsegment',
    ];

    // Check if filters contain any dynamic filter keys
    const hasDynamicFilters = dynamicFilterKeys.some((key) => filters[key] !== undefined);

    // If we have dynamic filters, pass them through as-is (with some normalization)
    if (hasDynamicFilters) {
      const transformed: Record<string, any> = {};

      // Pass through dynamic filters as-is
      dynamicFilterKeys.forEach((key) => {
        if (filters[key] !== undefined) {
          transformed[key] = Array.isArray(filters[key])
            ? filters[key]
            : filters[key]
              ? [filters[key]]
              : [];
        }
      });

      // Also include any other filters that might be present
      Object.keys(filters).forEach((key) => {
        if (!dynamicFilterKeys.includes(key) && filters[key] !== undefined) {
          // For non-dynamic filters, check if they're in segment filters config
          // If they are, pass them through; otherwise, use legacy transformation
          if (segmentFilters && segmentFilters[key]) {
            transformed[key] = Array.isArray(filters[key])
              ? filters[key]
              : filters[key]
                ? [filters[key]]
                : [];
          }
        }
      });

      return transformed;
    }

    // PRIORITY 1: Check JSON config first - if audience has filters in JSON, use those
    const audienceNameLower = audienceName?.toLowerCase() || '';
    const jsonConfig = Object.keys(segmentFiltersConfig.audienceFilters).find(
      (key) => key.toLowerCase() === audienceNameLower,
    );

    if (jsonConfig) {
      const config = (segmentFiltersConfig.audienceFilters as any)[jsonConfig];
      // If JSON has filters defined, use JSON-based transformation
      if (config && config.hasFilters && config.filters) {
        // Pass through filters as-is since they match the JSON config structure
        const transformed: Record<string, any> = {};
        Object.keys(filters).forEach((key) => {
          if (filters[key] !== undefined) {
            transformed[key] = Array.isArray(filters[key])
              ? filters[key]
              : filters[key]
                ? [filters[key]]
                : [];
          }
        });
        return transformed;
      }
    }

    // PRIORITY 2: Fallback to environment variable check (legacy support)
    if (
      audienceName &&
      import.meta.env.VITE_KNF_CUSTOM_FILTER_AUDIENCE_NAME &&
      import.meta.env.VITE_KNF_CUSTOM_FILTER_AUDIENCE_NAME.toLowerCase().includes(
        audienceName.toLowerCase(),
      )
    ) {
      // Special handling for kettle and fire (legacy)
      return {
        sub_segment: filters.subSegments || [],
        age: filters.age || [],
        geographics: filters.geographics || [],
        gender: filters.gender || [],
        income: filters.income || [],
        education: filters.education || [],
        family_status: filters.family_status || [],
      };
    }

    // PRIORITY 3: Default transformation for all other audiences
    return {
      age_group: filters.age_group || [],
      household_income: filters.household_income || [],
      geo_location: filters.geo_location || [],
      pets: filters.pets || [],
      children: filters.children || [],
      generation: filters.generation || [],
      numberOfChildren: filters.numberOfChildren || [],
      marital_status: filters.marital_status || [],
      subsegment: filters.subSegments || [],
      // Handle both B2B and B2C filter keys
      industryL1: filters.industryL1 || [],
      industryL2: filters.industryL2 || [],
      functions: filters.functions || [],
      roles: filters.roles || [],
    };
  };

  // Get audience configuration
  const audienceConfig = useMemo(() => {
    const audienceName = audienceData?.audienceName || '';
    const audienceNameLower = audienceName.toLowerCase();

    // Check for exact matches first (case-insensitive)
    const exactMatch = Object.keys(segmentFiltersConfig.audienceFilters).find(
      (key) => key.toLowerCase() === audienceNameLower,
    );
    if (exactMatch) {
      return (segmentFiltersConfig.audienceFilters as any)[exactMatch];
    }

    // Check for partial matches with improved logic
    const normalizedAudience = audienceName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const potentialMatches: string[] = [];

    Object.keys(segmentFiltersConfig.audienceFilters).forEach((key) => {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');

      // Check multiple matching strategies
      const exactNormalizedMatch = normalizedKey === normalizedAudience;
      const audienceContainsKey = normalizedAudience.includes(normalizedKey);
      const keyContainsAudience = normalizedKey.includes(normalizedAudience);

      // Special handling for Kettle & Fire variants
      if (normalizedAudience.includes('kettle') && normalizedAudience.includes('fire')) {
        // Check for all Kettle & Fire variants including v4
        const kettleMatch =
          normalizedKey === 'kettleandfirev4' ||
          normalizedKey === 'kettleandfire' ||
          normalizedKey === 'kettlefire' ||
          normalizedKey === 'kettle' ||
          (normalizedKey.includes('kettle') &&
            normalizedKey.includes('fire') &&
            normalizedKey.includes('v4'));

        if (kettleMatch || exactNormalizedMatch) {
          potentialMatches.push(key);
        }
        // Don't return early - continue to check for exact matches
        if (!exactNormalizedMatch) {
          return;
        }
      }

      const match = exactNormalizedMatch || audienceContainsKey || keyContainsAudience;
      if (match) {
        potentialMatches.push(key);
      }
    });

    // For Kettle & Fire variants, prefer more specific matches
    let matchedKey: string | undefined;
    if (
      normalizedAudience.includes('kettle') &&
      normalizedAudience.includes('fire') &&
      potentialMatches.length > 0
    ) {
      // Priority order: v4 variants > kettleandfire > kettlefire > kettle
      const v4Match = potentialMatches.find(
        (key) =>
          key.toLowerCase().includes('v4') ||
          key
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .includes('v4'),
      );
      if (v4Match) {
        matchedKey = v4Match;
      } else if (potentialMatches.includes('kettleandfire')) {
        matchedKey = 'kettleandfire';
      } else if (potentialMatches.includes('kettlefire')) {
        matchedKey = 'kettlefire';
      } else if (potentialMatches.includes('kettle')) {
        matchedKey = 'kettle';
      } else {
        // Use first match if no specific priority match found
        matchedKey = potentialMatches[0];
      }
    } else {
      // For other audiences, just use the first match
      matchedKey = potentialMatches[0];
    }

    if (matchedKey) {
      const config = (segmentFiltersConfig.audienceFilters as any)[matchedKey];
      return config;
    }

    // Default to simple type if no match found
    return { hasFilters: false, type: 'simple' };
  }, [audienceData?.audienceName]);

  const previewTags = useMemo(() => {
    return (segmentFiltersConfig.previewTags as any)[audienceConfig.type] || [];
  }, [audienceConfig]);

  const filterDefaults = useMemo(() => {
    return (segmentFiltersConfig.filterDefaults as any)[audienceConfig.type] || {};
  }, [audienceConfig]);

  // Fetch segments and custom segmentations on mount
  useEffect(() => {
    fetchSegments();
    loadCustomSegmentations();
  }, [audienceId]);

  // Load existing custom segmentations for this audience
  const loadCustomSegmentations = async () => {
    if (!audienceId) return;
    try {
      const segmentations = await segmentationService.listForAudience(audienceId);
      const completed = segmentations.filter((s) => s.status === 'completed');
      setCustomSegmentations(completed);
    } catch (error) {
      console.error('Failed to load custom segmentations:', error);
    }
  };

  // Track pending segments for auto-advance UI
  const [pendingSegmentData, setPendingSegmentData] = useState<{
    segments: any[];
    assignmentProgress?: { completed: number; total: number };
  } | null>(null);

  // Handle custom segmentation created - show only this segmentation's segments
  const handleCustomSegmentationComplete = (segmentation: Segmentation) => {
    setSelectedDynamicSegmentation(segmentation);
    // Set this as the active custom segmentation to display
    setActiveCustomSegmentation(segmentation);
    // Clear pending segment data since we now have final results
    setPendingSegmentData(null);
    // Return to static view to show the new segments
    setActiveTab('static');
  };

  // Handle segments discovered during processing (for progressive UI)
  const handleSegmentsDiscovered = (
    segments: any[],
    assignmentProgress?: { completed: number; total: number },
  ) => {
    setPendingSegmentData({ segments, assignmentProgress });
    // Switch to static tab to show cards in the same location where final results will appear
    if (segments && segments.length > 0 && activeTab === 'dynamic') {
      setActiveTab('static');
    }
  };

  // Handle viewing personas for a custom segment
  const handleCustomSegmentViewPersonas = async (segmentationId: number, segmentIndex: number) => {
    if (!activeCustomSegmentation) return;

    // Open modal immediately with loading state
    setLoadingPersonas(true);
    setSelectedPersona(null);
    setExpandedRoles({});
    setRolePersonaMap({}); // Clear previous data
    setPreviewTotal(null);
    setIsPersonaModalOpen(true);

    try {
      // Use existing segmentation service route
      const { personas, total } = await segmentationService.getPersonasBySegment(
        segmentationId,
        segmentIndex,
      );

      // Format like rolePersonaMap
      const roleToPersonasMap: Record<string, any[]> = { 'All Profiles': personas };
      setRolePersonaMap(roleToPersonasMap);
      setPreviewTotal(total);
    } catch (error) {
      console.error('Error viewing custom segment personas:', error);
    } finally {
      setLoadingPersonas(false);
    }
  };

  // Handle toggling a segment within a custom segmentation
  const handleCustomSegmentToggle = (
    segmentationId: number,
    segmentIndex: number,
    _personaIds: number[],
  ) => {
    setCustomSegmentSelections((prev) => {
      const current = prev[segmentationId] || [];
      const isSelected = current.includes(segmentIndex);
      const newIndices = isSelected
        ? current.filter((i) => i !== segmentIndex)
        : [...current, segmentIndex];
      return { ...prev, [segmentationId]: newIndices };
    });

    // Clear error when selecting
    if (error) setError(null);
    if (onEditStep) onEditStep();
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all pending timeouts to prevent memory leaks
      Object.values(filterUpdateTimeouts.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, []);

  // Initialize persona filters when segments change
  useEffect(() => {
    if (segments.length > 0) {
      const initialFilters: Record<number, any> = {};
      segments.forEach((segment) => {
        if (audienceConfig.hasFilters) {
          // Don't use empty filter defaults - start with empty objects so filters can be selected
          initialFilters[segment.id] = {};
        } else {
          // For simple audiences, use segment name as industryL1 filter
          initialFilters[segment.id] = { industryL1: [segment.name] };
        }
      });

      setPersonaFilters(initialFilters);
    }
  }, [segments, audienceConfig, filterDefaults]);

  const fetchSegments = async () => {
    if (!audienceId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/audience/${audienceId}/segments`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error('UnifiedSegmentsSelector - API returned non-array data:', data);
        throw new Error('Invalid data format received from segments API');
      }

      setSegments(data);

      // Re-check the segments the wizard already holds.
      //
      // This component keeps its checkboxes in local state and used to start every mount empty, so ANY
      // re-entry to the step lost the selection — including the Edit button on Review & Run, which is the
      // one path where the user has definitely already chosen. Reported as: "0 of 1 segments selected" with
      // Continue disabled, for a single-segment audience that had auto-selected its only segment.
      //
      // Filtered against the segments this audience actually returned, so a selection left over from a
      // different audience cannot tick a box that is not on screen. And only applied when there IS one:
      // writing an empty array here would be indistinguishable from the old behaviour on a first visit, but
      // pre-ticking anything would run a study against segments nobody picked.
      const alreadyChosen = (audienceData?.selectedSegments || []).filter((segmentId: number) =>
        data.some((segment) => segment.id === segmentId),
      );
      if (alreadyChosen.length > 0) {
        setSelectedSegments(alreadyChosen);
        alreadyChosen.forEach((segmentId: number) => fetchSegmentDemographics(segmentId));
      }

      // Initialize persona counts for all segments
      data.forEach((segment) => {
        setSegmentPersonaCounts((prev) => ({
          ...prev,
          [segment.id]: segment.len,
        }));
      });

      // Fetch demographics for all segments
      data.forEach((segment) => {
        fetchSegmentDemographics(segment.id);
      });
    } catch (err) {
      console.error('Error fetching segments:', err);
      setError('Failed to load segments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch segment demographics overview
  const fetchSegmentDemographics = async (segmentId: number) => {
    if (segmentDemographics[segmentId]) {
      return; // Already loaded
    }

    setLoadingDemographics((prev) => ({ ...prev, [segmentId]: true }));

    try {
      const response = await fetch(`${API_URL}/demographics/overview/segment/${segmentId}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setSegmentDemographics((prev) => ({ ...prev, [segmentId]: data.data }));
      } else {
        console.error(`🔍 Failed to fetch demographics for segment ${segmentId}:`, response.status);
      }
    } catch (error) {
      console.error(`🔍 Error fetching demographics for segment ${segmentId}:`, error);
    } finally {
      setLoadingDemographics((prev) => ({ ...prev, [segmentId]: false }));
    }
  };

  const updateSegmentPersonaCount = async (segmentId: number) => {
    // Always try to get persona count, regardless of filter configuration

    setLoadingCounts((prev) => ({ ...prev, [segmentId]: true }));

    try {
      // Use the current filter state to ensure we have the latest filters
      const currentFilters = personaFilters[segmentId] || {};
      const audienceName = audienceData?.audienceName || '';
      const segment = segments.find((s) => s.id === segmentId);
      const segmentFilterConfig = segment?.filters || {};

      // Use centralized transformation helper with segment filter config
      const transformedFilters = transformFiltersForBackend(
        currentFilters,
        audienceName,
        segmentFilterConfig,
      );

      const requestBody = {
        segments: [segmentId],
        filters: {
          [segmentId]: transformedFilters,
        },
        audience_name: audienceName,
      };

      const response = await fetch(`${API_URL}/filter_personas`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('UnifiedSegmentsSelector - filter_personas error response:', errorText);
        throw new Error(`Failed to fetch filtered count: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      const totalCount = Object.values(data).reduce(
        (sum: number, ids: any) => sum + (Array.isArray(ids) ? ids.length : 0),
        0,
      );

      setSegmentPersonaCounts((prev) => ({ ...prev, [segmentId]: totalCount }));
    } catch (err) {
      console.error('UnifiedSegmentsSelector - Error updating persona count:', err);
      // Set a default count if the request fails
      const segment = segments.find((s) => s.id === segmentId);
      if (segment) {
        setSegmentPersonaCounts((prev) => ({ ...prev, [segmentId]: segment.len || 0 }));
      }
    } finally {
      setLoadingCounts((prev) => ({ ...prev, [segmentId]: false }));
    }
  };

  // New function that takes filters as a parameter to avoid race conditions
  const updateSegmentPersonaCountWithFilters = async (segmentId: number, filters: any) => {
    // Always try to get persona count, regardless of filter configuration

    setLoadingCounts((prev) => ({ ...prev, [segmentId]: true }));

    try {
      const audienceName = audienceData?.audienceName || '';
      const segment = segments.find((s) => s.id === segmentId);
      const segmentFilterConfig = segment?.filters || {};

      // Use centralized transformation helper with segment filter config
      const transformedFilters = transformFiltersForBackend(
        filters,
        audienceName,
        segmentFilterConfig,
      );

      const requestBody = {
        segments: [segmentId],
        filters: {
          [segmentId]: transformedFilters,
        },
        audience_name: audienceName,
      };

      const response = await fetch(`${API_URL}/filter_personas`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('UnifiedSegmentsSelector - filter_personas error response:', errorText);
        throw new Error(`Failed to fetch filtered count: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      const totalCount = Object.values(data).reduce(
        (sum: number, ids: any) => sum + (Array.isArray(ids) ? ids.length : 0),
        0,
      );

      setSegmentPersonaCounts((prev) => ({ ...prev, [segmentId]: totalCount }));
    } catch (err) {
      console.error('UnifiedSegmentsSelector - Error updating persona count:', err);
      // Set a default count if the request fails
      const segment = segments.find((s) => s.id === segmentId);
      if (segment) {
        setSegmentPersonaCounts((prev) => ({ ...prev, [segmentId]: segment.len || 0 }));
      }
    } finally {
      setLoadingCounts((prev) => ({ ...prev, [segmentId]: false }));
    }
  };

  const handleSegmentToggle = (segmentId: number) => {
    const isCurrentlySelected = selectedSegments.includes(segmentId);

    if (isCurrentlySelected) {
      // Deselect segment
      setSelectedSegments((prev) => prev.filter((id) => id !== segmentId));
    } else {
      // Select segment
      setSelectedSegments((prev) => [...prev, segmentId]);

      // Clear any error when a segment is selected
      if (error) {
        setError(null);
      }

      // Fetch demographics for rich overview
      if (audienceConfig.type !== 'simple') {
        fetchSegmentDemographics(segmentId);
      }

      // Update persona count if filters are available
      if (audienceConfig.hasFilters) {
        updateSegmentPersonaCount(segmentId);
      }
    }

    if (onEditStep) {
      onEditStep();
    }
  };

  const handleFilterChange = useCallback(
    (segmentId: number, filterKey: string, values: string[]) => {
      // Clear any existing timeout for this segment
      if (filterUpdateTimeouts.current[segmentId]) {
        clearTimeout(filterUpdateTimeouts.current[segmentId]);
      }

      // Update the UI immediately for better responsiveness
      setPersonaFilters((prev) => {
        const newFilters = {
          ...prev,
          [segmentId]: {
            ...prev[segmentId],
            [filterKey]: values,
          },
        };

        // Store the pending update with the new state
        pendingFilterUpdates.current[segmentId] = { ...newFilters[segmentId] };

        return newFilters;
      });

      // Debounce the API call to prevent race conditions
      filterUpdateTimeouts.current[segmentId] = setTimeout(() => {
        // Get the final state of filters for this segment
        const finalFilters = pendingFilterUpdates.current[segmentId];
        if (finalFilters) {
          // Update persona count with the pending filters directly
          updateSegmentPersonaCountWithFilters(segmentId, finalFilters);
          // Clear the pending updates
          delete pendingFilterUpdates.current[segmentId];
        }
      }, 300); // Increased delay to 300ms for better debouncing

      if (onEditStep) {
        onEditStep();
      }
    },
    [onEditStep],
  );

  const handleInfoHover = (segmentId: number, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setShowInfoTooltip({
      segmentId,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleInfoLeave = () => {
    setShowInfoTooltip({ segmentId: null, x: 0, y: 0 });
  };

  const handleViewPersonas = async (segmentId: number) => {
    try {
      setSelectedSegmentForPersonas(segmentId);
      setLoadingPersonas(true);
      setPreviewTotal(null); // predefined view loads the full list; no cap banner

      // Reset persona selection
      setSelectedPersona(null);

      // Ensure all dropdowns are closed when dialog opens
      setExpandedRoles({});

      try {
        // Fetch filtered persona IDs first
        await fetchFilteredPersonaIds(segmentId);

        // Clear previous persona data

        // Open the modal after data is ready
        setIsPersonaModalOpen(true);
      } catch (error) {
        console.error('Error in handleViewPersonas:', error);
        console.error('Error details:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : 'No stack trace',
          segmentId,
        });
      }
    } catch (error) {
      console.error('Error in handleViewPersonas outer try block:', error);
    } finally {
      setLoadingPersonas(false);
    }
  };

  // Function to select all segments and all filters
  const handleSelectAll = async () => {
    setSelectAllLoading(true);

    try {
      // First, select all segments
      setSelectedSegments(segments.map((segment) => segment.id));

      // Then, select all filters for each segment
      const allFiltersObj: Record<number, any> = {};

      for (const segment of segments) {
        // Create an object with all possible filters selected based on audience config
        const filters = audienceConfig.filters || {};
        const segmentFilters: Record<string, string[]> = {};

        Object.entries(filters).forEach(([filterKey, config]: [string, any]) => {
          if (config.options) {
            if (
              config.type === 'hierarchical' &&
              typeof config.options === 'object' &&
              !Array.isArray(config.options)
            ) {
              // For hierarchical filters, collect all sub-options from all parents
              const allSubOptions: string[] = [];
              Object.values(config.options).forEach((subOptions: any) => {
                if (Array.isArray(subOptions)) {
                  allSubOptions.push(...subOptions);
                }
              });
              segmentFilters[filterKey] = allSubOptions;
            } else if (Array.isArray(config.options)) {
              segmentFilters[filterKey] = [...config.options];
            }
          }
        });

        allFiltersObj[segment.id] = segmentFilters;

        // Update persona count for this segment with all filters
        await updateSegmentPersonaCountWithFilters(segment.id, segmentFilters);
      }

      // Update filters state
      setPersonaFilters(allFiltersObj);

      // Notify parent of changes
      if (onEditStep) {
        onEditStep();
      }
    } catch (error) {
      console.error('Error selecting all segments:', error);
    } finally {
      setSelectAllLoading(false);
    }
  };

  // New function to fetch filtered persona IDs
  const fetchFilteredPersonaIds = async (segmentId: number) => {
    setLoadingPersonaIds(true);

    try {
      // Create a filter request specific to this segment
      const segmentFilters = personaFilters[segmentId] || {};
      const audienceName = audienceData?.audienceName || '';
      const segment = segments.find((s) => s.id === segmentId);
      const segmentFilterConfig = segment?.filters || {};

      // Use centralized transformation helper - ensures consistency with count function
      const transformedFilters = transformFiltersForBackend(
        segmentFilters,
        audienceName,
        segmentFilterConfig,
      );

      const filterRequest = {
        segments: [segmentId],
        filters: {
          [segmentId]: transformedFilters,
        },
        audience_name: audienceName,
      };

      console.log('🔍 View Personas - Fetching filtered persona IDs:', {
        segmentId,
        audienceName,
        rawFilters: segmentFilters,
        transformedFilters,
        requestBody: filterRequest,
      });

      const response = await fetch(`${API_URL}/filter_personas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(filterRequest),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch filtered personas');
      }

      // Response format: { role1: [PersonaType, ...], role2: [...], ... }
      // Each persona object includes: id, name, sidebar fields, bio, demographic
      const roleToPersonasMap = await response.json();

      // Store directly - no need to fetch individual personas anymore
      setRolePersonaMap(roleToPersonasMap);

      return roleToPersonasMap;
    } catch (error) {
      console.error('Error fetching filtered persona IDs:', error);
      return {};
    } finally {
      setLoadingPersonaIds(false);
    }
  };

  // Function to toggle role expansion
  const toggleRoleExpansion = (role: string) => {
    try {
      // Close all other roles first to implement exclusive expansion
      const newExpandedRoles: Record<string, boolean> = {};
      // Toggle the clicked role
      newExpandedRoles[role] = !expandedRoles[role];
      // Update the expanded roles state
      setExpandedRoles(newExpandedRoles);
    } catch (error) {
      console.error(`Error in toggleRoleExpansion for role ${role}:`, error);
    }
  };

  // Function to get personas for a specific role
  const getPersonasForRole = (role: string): PersonaType[] => {
    try {
      // rolePersonaMap now contains full persona objects directly
      return rolePersonaMap[role] || [];
    } catch (error) {
      console.error(`Error in getPersonasForRole for role ${role}:`, error);
      return [];
    }
  };

  // Generic recursive function to render persona data
  const renderPersonaData = (
    data: any,
    title: string,
    bgColor: string,
    borderColor: string,
    textColor: string,
  ) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return null;

    // Helper function to format objects intelligently
    const formatObject = (obj: any): string => {
      // Handle common persona field patterns
      if (obj.name && obj.proficiency_level) {
        return `${obj.name} (${obj.proficiency_level})`;
      }
      if (obj.platform && obj.usage_level) {
        return `${obj.platform}: ${obj.usage_level}${obj.content_preference ? ` - ${obj.content_preference}` : ''}`;
      }
      if (obj.level && obj.field) {
        return `${obj.level} in ${obj.field}`;
      }
      if (obj.region && obj.demographic_context) {
        return `${obj.region} - ${obj.demographic_context}`;
      }
      if (obj.genre && obj.artists) {
        return `${obj.genre}: ${Array.isArray(obj.artists) ? obj.artists.join(', ') : obj.artists}`;
      }
      // Default: return a formatted string of key-value pairs
      const parts = Object.entries(obj)
        .filter(([_, value]) => value !== null && value !== undefined)
        .map(([key, value]) => {
          const formattedKey = key.replace(/_/g, ' ');
          if (typeof value === 'object' && !Array.isArray(value)) {
            return `${formattedKey}: ${formatObject(value)}`;
          }
          if (Array.isArray(value)) {
            return `${formattedKey}: ${value.map((v) => (typeof v === 'object' ? formatObject(v) : v)).join(', ')}`;
          }
          return `${formattedKey}: ${value}`;
        });
      return parts.join('; ');
    };

    const renderItem = (item: any, index: number, depth: number = 0) => {
      // Handle strings
      if (typeof item === 'string') {
        return (
          <li key={index} className="text-sm text-gray-700">
            {item}
          </li>
        );
      }
      // Handle objects
      else if (typeof item === 'object' && item !== null) {
        // Check if it's a simple object that can be formatted inline
        const hasSimpleStructure =
          Object.keys(item).length <= 3 &&
          !Object.values(item).some((v) => typeof v === 'object' && v !== null);

        if (hasSimpleStructure || depth > 1) {
          // Format simple objects inline
          return (
            <li key={index} className="text-sm text-gray-700">
              {formatObject(item)}
            </li>
          );
        } else {
          // For complex objects, render expanded view
          return (
            <li key={index} className="text-sm text-gray-700 mb-2">
              {Object.entries(item).map(([key, value]) => (
                <div key={key} className="ml-4 mb-1">
                  <span className="font-medium text-gray-600 capitalize">
                    {key.replace(/_/g, ' ')}:{' '}
                  </span>
                  {typeof value === 'string' ||
                  typeof value === 'number' ||
                  typeof value === 'boolean' ? (
                    <span>{String(value)}</span>
                  ) : Array.isArray(value) ? (
                    <ul className="list-disc ml-4 mt-1">
                      {value.map((subItem, subIndex) => renderItem(subItem, subIndex, depth + 1))}
                    </ul>
                  ) : typeof value === 'object' && value !== null ? (
                    <span className="text-gray-600">{formatObject(value)}</span>
                  ) : (
                    <span>{String(value)}</span>
                  )}
                </div>
              ))}
            </li>
          );
        }
      }
      // Handle other types (numbers, booleans, etc.)
      else {
        return (
          <li key={index} className="text-sm text-gray-700">
            {String(item)}
          </li>
        );
      }
    };

    return (
      <div className={`${bgColor} p-5 rounded-lg border ${borderColor}`}>
        <h4 className={`font-medium ${textColor} mb-3 flex items-center`}>
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
          {title}
        </h4>
        <ul className="list-disc ml-5 space-y-1.5">
          {Array.isArray(data)
            ? data.map((item, index) => renderItem(item, index, 0))
            : renderItem(data, 0, 0)}
        </ul>
      </div>
    );
  };

  // Dynamic function to render all persona fields
  const renderAllPersonaFields = (persona: PersonaType) => {
    // Fields to exclude from dynamic rendering (already shown in header, sidebar, or bio/demographic sections)
    const excludedFields = [
      'id',
      'name',
      'created_at',
      'updated_at',
      'segment_id',
      'cluster_id',
      'cluster_size',
      'filters',
      'validation_status',
      'modified_at',
      '4_word_description',
      'four_word_description',
      'bio',
      'demographic',
      'demographics',
      'occupation',
      'job_title',
      'company_name',
      'category',
    ];

    // Basic info fields (simple strings/numbers)
    const basicInfoFields = [
      'age',
      'gender',
      'job_title',
      'company_name',
      'income',
      'education',
      'location',
    ];

    // Professional fields
    const professionalFields = ['industry_l1', 'sub_industry_l2', 'function', 'role'];

    // Create a flattened persona object that includes data from the 'data' field if it exists
    const flattenedPersona = { ...persona };
    if (persona.data && typeof persona.data === 'object') {
      Object.entries(persona.data).forEach(([key, value]) => {
        if (!flattenedPersona[key]) {
          flattenedPersona[key] = value;
        }
      });
    }

    // All other fields (arrays and objects)
    const dynamicFields: { [key: string]: any } = {};

    Object.entries(flattenedPersona).forEach(([key, value]) => {
      if (
        !excludedFields.includes(key) &&
        !basicInfoFields.includes(key) &&
        !professionalFields.includes(key) &&
        key !== 'data' && // Exclude the data field itself since we've flattened it
        value !== null &&
        value !== undefined &&
        value !== ''
      ) {
        dynamicFields[key] = value;
      }
    });

    // Color schemes for different sections
    const colorSchemes = [
      { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-800' },
      { bg: 'bg-rose-50', border: 'border-rose-100', text: 'text-rose-800' },
      { bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-800' },
      { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-800' },
      { bg: 'bg-green-50', border: 'border-green-100', text: 'text-green-800' },
      { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-800' },
      { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-800' },
      { bg: 'bg-pink-50', border: 'border-pink-100', text: 'text-pink-800' },
      { bg: 'bg-cyan-50', border: 'border-cyan-100', text: 'text-cyan-800' },
      { bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-800' },
    ];

    const basicInfo: { [key: string]: any } = {};
    const professionalInfo: { [key: string]: any } = {};

    // Collect basic info
    basicInfoFields.forEach((field) => {
      if (flattenedPersona[field] && flattenedPersona[field] !== '') {
        basicInfo[field] = flattenedPersona[field];
      }
    });

    // Collect professional info
    professionalFields.forEach((field) => {
      if (flattenedPersona[field] && flattenedPersona[field] !== '') {
        professionalInfo[field] = flattenedPersona[field];
      }
    });

    // Helper function to format values based on their type
    const formatValue = (value: any): string => {
      if (value === null || value === undefined) {
        return '';
      }

      // Handle strings and numbers directly
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }

      // Helper to format objects
      const formatObject = (obj: any): string => {
        // Handle common persona field patterns
        if (obj.name && obj.proficiency_level) {
          return `${obj.name} (${obj.proficiency_level})`;
        }
        if (obj.platform && obj.usage_level) {
          return `${obj.platform}: ${obj.usage_level}${obj.content_preference ? ` - ${obj.content_preference}` : ''}`;
        }
        if (obj.level && obj.field) {
          return `${obj.level} in ${obj.field}`;
        }
        if (obj.region && obj.demographic_context) {
          return `${obj.region} - ${obj.demographic_context}`;
        }
        if (obj.genre && obj.artists) {
          return `${obj.genre}: ${Array.isArray(obj.artists) ? obj.artists.join(', ') : obj.artists}`;
        }
        // Default: return a formatted string of key-value pairs
        const parts = Object.entries(obj)
          .filter(([_, value]) => value !== null && value !== undefined)
          .map(([key, value]) => {
            const formattedKey = key.replace(/_/g, ' ');
            if (typeof value === 'object' && !Array.isArray(value)) {
              return `${formattedKey}: ${formatObject(value)}`;
            }
            if (Array.isArray(value)) {
              return `${formattedKey}: ${value.map((v) => (typeof v === 'object' ? formatObject(v) : v)).join(', ')}`;
            }
            return `${formattedKey}: ${value}`;
          });
        return parts.join('; ');
      };

      // Handle arrays
      if (Array.isArray(value)) {
        return value
          .map((item) => {
            if (typeof item === 'object' && item !== null) {
              return formatObject(item);
            }
            return String(item);
          })
          .join(', ');
      }

      // Handle objects
      if (typeof value === 'object') {
        return formatObject(value);
      }

      return String(value);
    };

    return (
      <div className="space-y-6">
        {/* Bio Section - Show if bio exists */}
        {flattenedPersona.bio && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#07E5D1] to-[#06D4C2] flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-[#1C1C1E] text-base mb-2">About</h4>
                  <p className="text-[#374151] leading-relaxed text-[15px]">
                    {flattenedPersona.bio}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Demographic Section - Show if demographic exists */}
        {flattenedPersona.demographic &&
          typeof flattenedPersona.demographic === 'object' &&
          Object.keys(flattenedPersona.demographic).length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-6">
                <h4 className="font-semibold text-[#1C1C1E] text-base mb-5 flex items-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#07E5D1] to-[#06D4C2] flex items-center justify-center mr-3">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  Profile Details
                </h4>

                {/* Key Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {flattenedPersona.demographic.age && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 text-center border border-blue-100">
                      <div className="text-2xl font-bold text-blue-600">
                        {flattenedPersona.demographic.age}
                      </div>
                      <div className="text-xs text-blue-600/70 font-medium mt-1">Years Old</div>
                    </div>
                  )}
                  {flattenedPersona.demographic.householdIncome && (
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 text-center border border-emerald-100">
                      <div className="text-lg font-bold text-emerald-600">
                        {flattenedPersona.demographic.householdIncome}
                      </div>
                      <div className="text-xs text-emerald-600/70 font-medium mt-1">Income</div>
                    </div>
                  )}
                  {flattenedPersona.demographic.educationLevel && (
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 text-center border border-purple-100">
                      <div className="text-sm font-bold text-purple-600">
                        {flattenedPersona.demographic.educationLevel}
                      </div>
                      <div className="text-xs text-purple-600/70 font-medium mt-1">Education</div>
                    </div>
                  )}
                  {(flattenedPersona.demographic.locality ||
                    flattenedPersona.demographic.region) && (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 text-center border border-amber-100">
                      <div className="text-sm font-bold text-amber-600">
                        {flattenedPersona.demographic.locality ||
                          flattenedPersona.demographic.region}
                      </div>
                      <div className="text-xs text-amber-600/70 font-medium mt-1">Location</div>
                    </div>
                  )}
                </div>

                {/* Detailed Info Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  {Object.entries(flattenedPersona.demographic)
                    .filter(
                      ([key]) =>
                        ![
                          'age',
                          'householdIncome',
                          'educationLevel',
                          'locality',
                          'region',
                        ].includes(key),
                    )
                    .map(([key, value]) => {
                      const icons: Record<string, string> = {
                        occupation: '💼',
                        gender: '👤',
                        maritalStatus: '💍',
                        children: '👶',
                        homeOwner: '🏠',
                        employmentStatus: '📋',
                        industry: '🏢',
                        seniority: '📈',
                        companySize: '🏛️',
                        neighborhoodType: '🏘️',
                        livingSituation: '🛋️',
                        petsOwned: '🐾',
                        raceEthnicity: '🌍',
                        smallBusinessOwner: '🏪',
                      };
                      return (
                        <div
                          key={key}
                          className="flex items-center py-2 border-b border-gray-100 last:border-0"
                        >
                          <span className="text-lg mr-3">{icons[key] || '📌'}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </div>
                            <div className="text-sm font-medium text-[#1C1C1E]">
                              {Array.isArray(value) ? value.join(', ') : String(value)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

        {/* Basic Information - Only show if bio doesn't exist */}
        {!flattenedPersona.bio && Object.keys(basicInfo).length > 0 && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50/50 p-5 rounded-lg border border-blue-100">
              <h4 className="font-medium text-blue-800 mb-4 flex items-center">
                <User className="w-4 h-4 mr-2" />
                Personal Information
              </h4>
              <div className="space-y-3">
                {Object.entries(basicInfo).map(([key, value]) => (
                  <div key={key} className="flex items-start">
                    <span className="text-sm font-medium text-gray-600 w-28 capitalize">
                      {key.replace(/_/g, ' ')}:
                    </span>
                    <span className="text-sm text-gray-800">{formatValue(value)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Professional Information */}
            {Object.keys(professionalInfo).length > 0 && (
              <div className="bg-indigo-50/50 p-5 rounded-lg border border-indigo-100">
                <h4 className="font-medium text-indigo-800 mb-4 flex items-center">
                  <Building className="w-4 h-4 mr-2" />
                  Professional Details
                </h4>
                <div className="space-y-3">
                  {Object.entries(professionalInfo).map(([key, value]) => (
                    <div key={key} className="flex items-start">
                      <span className="text-sm font-medium text-gray-600 w-28 capitalize">
                        {key.replace(/_/g, ' ')}:
                      </span>
                      <span className="text-sm text-gray-800">{formatValue(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dynamic Fields - Only show if bio doesn't exist */}
        {!flattenedPersona.bio && Object.keys(dynamicFields).length > 0 && (
          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(dynamicFields).map(([key, value], index) => {
              const colorScheme = colorSchemes[index % colorSchemes.length];
              const formattedTitle = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase());

              return (
                <div key={key}>
                  {renderPersonaData(
                    value,
                    formattedTitle,
                    colorScheme.bg,
                    colorScheme.border,
                    colorScheme.text,
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleNext = () => {
    // Handle dynamic segmentation tab
    if (activeTab === 'dynamic') {
      // Case 1: No segmentation yet - trigger creation
      if (!selectedDynamicSegmentation) {
        if (dynamicPanelRef.current?.isProcessing()) {
          // Already processing, don't do anything
          return;
        }
        if (!dynamicPanelRef.current?.isReady()) {
          setError('Please enter a segmentation query first.');
          return;
        }
        setError(null);
        dynamicPanelRef.current?.createSegmentation();
        return;
      }

      // Case 2: Segmentation exists but no segments selected
      if (selectedDynamicIndices.length === 0) {
        setError('Please select at least one dynamic segment to continue.');
        return;
      }

      // Case 3: Segmentation exists and segments selected - navigate
      setError(null);

      // Get segment names for the selected indices
      const segmentNames = selectedDynamicIndices
        .map((idx) => selectedDynamicSegmentation.segment_definitions?.[idx]?.label)
        .filter((name): name is string => !!name);

      const dynamicData: DynamicSegmentationData = {
        dynamic_segmentation_id: selectedDynamicSegmentation.id,
        dynamic_segment_indices: selectedDynamicIndices,
        persona_ids: selectedDynamicPersonaIds,
        segment_names: segmentNames,
      };

      console.log('🔍 Dynamic Segmentation - Passing to parent:', dynamicData);

      onNext([], {}, dynamicData);
      return;
    }

    // Handle static segmentation tab
    // Check for selections based on current view
    const hasCustomSelections = activeCustomSegmentation
      ? (customSegmentSelections[activeCustomSegmentation.id] || []).length > 0
      : Object.values(customSegmentSelections).some((indices) => indices.length > 0);
    const hasPredefinedSelections = selectedSegments.length > 0;

    // When viewing custom segments, only check custom selections
    const hasRequiredSelection = activeCustomSegmentation
      ? hasCustomSelections
      : hasPredefinedSelections || hasCustomSelections;

    if (!hasRequiredSelection) {
      setError(
        'Please select at least one segment to continue. Use the checkboxes to select segments from the list above.',
      );
      return;
    }

    // Clear any existing error when proceeding successfully
    setError(null);

    // Transform filters for backend before passing to parent
    const audienceName = audienceData?.audienceName || '';
    const transformedPersonaFilters: Record<number, any> = {};

    selectedSegments.forEach((segmentId) => {
      const filters = personaFilters[segmentId] || {};
      const segment = segments.find((s) => s.id === segmentId);
      const segmentFilterConfig = segment?.filters || {};
      transformedPersonaFilters[segmentId] = transformFiltersForBackend(
        filters,
        audienceName,
        segmentFilterConfig,
      );
    });

    // Build custom segment data if any custom segments are selected
    let dynamicData: DynamicSegmentationData | undefined;
    if (activeCustomSegmentation && hasCustomSelections) {
      // Viewing custom segmentation results - use activeCustomSegmentation
      const selectedIndices = customSegmentSelections[activeCustomSegmentation.id] || [];
      const selectedPersonaIds: number[] = [];
      const selectedSegmentNames: string[] = [];

      selectedIndices.forEach((idx) => {
        const segment = activeCustomSegmentation.segment_definitions?.[idx];
        if (segment) {
          selectedPersonaIds.push(...segment.persona_ids);
          selectedSegmentNames.push(segment.label);
        }
      });

      dynamicData = {
        dynamic_segmentation_id: activeCustomSegmentation.id,
        dynamic_segment_indices: selectedIndices,
        persona_ids: selectedPersonaIds,
        segment_names: selectedSegmentNames,
      };
    } else if (hasCustomSelections) {
      // Mixed mode - collect from all custom segmentations
      const allCustomPersonaIds: number[] = [];
      const allCustomSegmentNames: string[] = [];

      Object.entries(customSegmentSelections).forEach(([segIdStr, indices]) => {
        if (indices.length === 0) return;
        const segmentation = customSegmentations.find((s) => s.id === Number(segIdStr));
        if (!segmentation) return;

        indices.forEach((idx) => {
          const segment = segmentation.segment_definitions?.[idx];
          if (segment) {
            allCustomPersonaIds.push(...segment.persona_ids);
            allCustomSegmentNames.push(segment.label);
          }
        });
      });

      const firstSelectedSegId = Object.entries(customSegmentSelections).find(
        ([_, indices]) => indices.length > 0,
      )?.[0];

      if (firstSelectedSegId) {
        dynamicData = {
          dynamic_segmentation_id: Number(firstSelectedSegId),
          dynamic_segment_indices: customSegmentSelections[Number(firstSelectedSegId)] || [],
          persona_ids: allCustomPersonaIds,
          segment_names: allCustomSegmentNames,
        };
      }
    }

    console.log('🔍 Segment Selection - Passing filters to parent:', {
      audienceName,
      selectedSegments,
      rawFilters: personaFilters,
      transformedFilters: transformedPersonaFilters,
      customSegmentSelections,
      dynamicData,
    });

    onNext(selectedSegments, transformedPersonaFilters, dynamicData);
  };

  const renderTooltip = () => {
    if (!showInfoTooltip.segmentId) return null;

    const segment = segments.find((s) => s.id === showInfoTooltip.segmentId);
    if (!segment) return null;

    return (
      <div
        className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm"
        style={{
          left: showInfoTooltip.x - 150,
          top: showInfoTooltip.y - 20,
          transform: 'translateY(-100%)',
        }}
      >
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-900">{segment.name}</h4>
          <p className="text-sm text-gray-600">{segment.description}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Users className="h-3 w-3" />
            <span>{segment.len} personas available</span>
          </div>
        </div>
        <div className="absolute bottom-0 left-1/2 transform translate-y-full -translate-x-1/2">
          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <StepContainer title="Loading Segments..." className="animate-fadeIn">
        <div className="flex items-center justify-center py-12">
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"></div>
            <div
              className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"
              style={{ animationDelay: '0.1s' }}
            ></div>
            <div
              className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"
              style={{ animationDelay: '0.2s' }}
            ></div>
          </div>
        </div>
      </StepContainer>
    );
  }

  return (
    <StepContainer
      title={
        activeTab === 'dynamic'
          ? ''
          : activeCustomSegmentation
            ? 'Your Custom Segments'
            : 'Select Your Target Segments'
      }
      subtitle={
        activeTab === 'dynamic' || activeCustomSegmentation
          ? ''
          : 'Built from real human interviews, social signals and survey data'
      }
      className="animate-fadeIn"
    >
      {/* Tab Switcher for Dynamic Segmentation - Hidden for now, defaulting to dynamic */}
      {/* {enableDynamicSegmentation && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('static')}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all",
              activeTab === 'static'
                ? "bg-[#07E5D1] text-white"
                : "bg-gray-100 text-[#374151] hover:bg-gray-200"
            )}
          >
            Pre-built Segments
          </button>
          <button
            onClick={() => setActiveTab('dynamic')}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5",
              activeTab === 'dynamic'
                ? "bg-[#07E5D1] text-white"
                : "bg-gray-100 text-[#374151] hover:bg-gray-200"
            )}
          >
            <Sparkles className="w-4 h-4" />
            Dynamic Segmentation
          </button>
        </div>
      )} */}

      {activeTab === 'static' ? (
        <>
          {/* Show processing state with skeleton cards OR custom segmentation results OR predefined segments */}
          {isDynamicProcessing && pendingSegmentData?.segments ? (
            <>
              {/* Processing View - shows in same location as final cards */}
              <div className="mb-6">
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                  <span>Creating custom segments...</span>
                  {pendingSegmentData.assignmentProgress && (
                    <span className="text-[#6B7280]">
                      ({pendingSegmentData.assignmentProgress.completed} /{' '}
                      {pendingSegmentData.assignmentProgress.total} personas assigned)
                    </span>
                  )}
                </div>
              </div>

              {/* Skeleton/Live Segment Cards during processing */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {pendingSegmentData.segments
                  .filter(
                    (seg) => seg.label && seg.label.trim() && seg.label.toLowerCase() !== 'unknown',
                  )
                  .map((segment, idx) => (
                    <div
                      key={`pending-${idx}`}
                      className="border border-gray-200 rounded-lg p-4 bg-white"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-medium text-gray-900">{segment.label}</h3>
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm text-gray-600">
                          {segment.persona_count !== null && segment.persona_count !== undefined ? (
                            <span>{segment.persona_count} profiles</span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-[#07E5D1] rounded-full animate-pulse" />
                              <span
                                className="w-1.5 h-1.5 bg-[#07E5D1] rounded-full animate-pulse"
                                style={{ animationDelay: '0.2s' }}
                              />
                              <span
                                className="w-1.5 h-1.5 bg-[#07E5D1] rounded-full animate-pulse"
                                style={{ animationDelay: '0.4s' }}
                              />
                              <span className="ml-1 text-[#6B7280]">counting...</span>
                            </span>
                          )}
                        </div>
                      </div>
                      {segment.description && (
                        <div className="text-sm text-gray-600 leading-relaxed">
                          {segment.description}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </>
          ) : activeCustomSegmentation ? (
            <>
              {/* Custom Segmentation Results View */}
              <div className="mb-6">
                <div className="text-sm text-gray-600">
                  {activeCustomSegmentation.total_personas_analyzed} personas analyzed
                </div>
              </div>

              {/* Analysis Summary */}
              {activeCustomSegmentation.analysis_summary && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-[#374151]">
                    {activeCustomSegmentation.analysis_summary}
                  </p>
                </div>
              )}

              {/* Custom Segments as Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {(activeCustomSegmentation.segment_definitions || [])
                  .filter(
                    (seg) =>
                      seg.label &&
                      seg.label.trim() &&
                      seg.label.toLowerCase() !== 'unknown' &&
                      seg.persona_count > 0,
                  )
                  .map((segment) => (
                    <CustomSegmentCard
                      key={`${activeCustomSegmentation.id}-${segment.index}`}
                      segment={segment}
                      segmentIndex={segment.index}
                      segmentationId={activeCustomSegmentation.id}
                      segmentationAxis={activeCustomSegmentation.segmentation_axis}
                      totalPersonasInSegmentation={
                        activeCustomSegmentation.total_personas_analyzed || 0
                      }
                      isSelected={(
                        customSegmentSelections[activeCustomSegmentation.id] || []
                      ).includes(segment.index)}
                      onToggle={handleCustomSegmentToggle}
                      onViewPersonas={handleCustomSegmentViewPersonas}
                    />
                  ))}
              </div>
            </>
          ) : (
            <>
              {/* Predefined Segments View */}
              <div className="mb-6 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Choose which segments of {audienceData?.audienceName || 'your audience'} to
                  include
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={loading || selectAllLoading || segments.length === 0}
                  className="bg-blue-400 text-white hover:bg-blue-500"
                >
                  {selectAllLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full"></div>
                      Selecting all...
                    </div>
                  ) : (
                    'Select All Segments'
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {segments.map((segment) => {
                  const totalAudienceSize = segments.reduce((total, seg) => total + seg.len, 0);

                  return (
                    <SegmentCard
                      key={segment.id}
                      segment={segment}
                      isSelected={selectedSegments.includes(segment.id)}
                      demographics={segmentDemographics[segment.id]}
                      loadingDemographics={loadingDemographics[segment.id]}
                      previewTags={previewTags}
                      audienceType={audienceConfig.type as 'b2b' | 'b2c' | 'simple'}
                      personaCount={segmentPersonaCounts[segment.id]}
                      loadingCount={loadingCounts[segment.id]}
                      totalAudienceSize={totalAudienceSize}
                      onToggle={handleSegmentToggle}
                      onInfoHover={handleInfoHover}
                      onInfoLeave={handleInfoLeave}
                      onViewPersonas={handleViewPersonas}
                      filterConfig={audienceConfig.filters || segment.filters || {}}
                      filterValues={personaFilters[segment.id] || {}}
                      onFilterChange={handleFilterChange}
                      onUpdateCount={updateSegmentPersonaCount}
                      onEditStep={onEditStep}
                      className="h-auto"
                    />
                  );
                })}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="space-y-4">
          <DynamicSegmentationPanel
            ref={dynamicPanelRef}
            audienceId={audienceId}
            onSegmentationComplete={handleCustomSegmentationComplete}
            onSegmentsDiscovered={handleSegmentsDiscovered}
            onSelectSegments={(indices, personaIds) => {
              setSelectedDynamicIndices(indices);
              setSelectedDynamicPersonaIds(personaIds);
            }}
            onProcessingChange={setIsDynamicProcessing}
            selectedSegmentIndices={selectedDynamicIndices}
          />
        </div>
      )}

      {/* Navigation */}
      <div className="pt-6 border-t border-gray-200">
        {/* Error message at bottom */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <svg
              className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <div>
              <p className="text-red-800 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {activeTab === 'static' ? (
              (() => {
                // If viewing custom segmentation results
                if (activeCustomSegmentation) {
                  const selectedIndices =
                    customSegmentSelections[activeCustomSegmentation.id] || [];
                  const selectedPersonaCount = selectedIndices.reduce((sum, idx) => {
                    const segment = activeCustomSegmentation.segment_definitions?.[idx];
                    return sum + (segment?.persona_count || 0);
                  }, 0);
                  const totalSegments = (activeCustomSegmentation.segment_definitions || []).filter(
                    (seg) =>
                      seg.label &&
                      seg.label.trim() &&
                      seg.label.toLowerCase() !== 'unknown' &&
                      seg.persona_count > 0,
                  ).length;

                  return (
                    <>
                      <div>
                        {selectedIndices.length} of {totalSegments} segments selected
                      </div>
                      {selectedIndices.length > 0 && (
                        <div className="text-xs mt-1">Total: {selectedPersonaCount} personas</div>
                      )}
                    </>
                  );
                }

                // Predefined segments view
                const customSelectedCount = Object.values(customSegmentSelections).reduce(
                  (sum, indices) => sum + indices.length,
                  0,
                );
                const customPersonaCount = Object.entries(customSegmentSelections).reduce(
                  (sum, [segIdStr, indices]) => {
                    const segmentation = customSegmentations.find((s) => s.id === Number(segIdStr));
                    if (!segmentation) return sum;
                    return (
                      sum +
                      indices.reduce((acc, idx) => {
                        const segment = segmentation.segment_definitions?.[idx];
                        return acc + (segment?.persona_count || 0);
                      }, 0)
                    );
                  },
                  0,
                );
                const totalSelectedCount = selectedSegments.length + customSelectedCount;

                return (
                  <>
                    <div>
                      {selectedSegments.length} of {segments.length} segments selected
                    </div>
                    {totalSelectedCount > 0 && (
                      <div className="text-xs mt-1">
                        {(() => {
                          const isAnySegmentLoading = selectedSegments.some(
                            (segmentId) => loadingCounts[segmentId],
                          );

                          if (isAnySegmentLoading) {
                            return (
                              <div className="flex items-center">
                                <span>Total: </span>
                                <div className="flex space-x-1 items-center ml-1">
                                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                                  <div
                                    className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"
                                    style={{ animationDelay: '0.2s' }}
                                  ></div>
                                  <div
                                    className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"
                                    style={{ animationDelay: '0.4s' }}
                                  ></div>
                                </div>
                              </div>
                            );
                          } else {
                            const predefinedPersonas = selectedSegments.reduce(
                              (sum, segmentId) =>
                                sum +
                                (segmentPersonaCounts[segmentId] !== undefined
                                  ? segmentPersonaCounts[segmentId]
                                  : 0),
                              0,
                            );
                            const totalPersonas = predefinedPersonas + customPersonaCount;
                            return `Total: ${totalPersonas} personas`;
                          }
                        })()}
                      </div>
                    )}
                  </>
                );
              })()
            ) : (
              <>
                <div>
                  {selectedDynamicIndices.length} dynamic segment
                  {selectedDynamicIndices.length !== 1 ? 's' : ''} selected
                  {selectedDynamicSegmentation &&
                    ` from "${selectedDynamicSegmentation.segmentation_axis}"`}
                </div>
                {selectedDynamicIndices.length > 0 && (
                  <div className="text-xs mt-1">
                    Total: {selectedDynamicPersonaIds.length} personas
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                if (activeTab === 'dynamic') {
                  // From prompt screen, go back to predefined segments
                  setActiveTab('static');
                  setActiveCustomSegmentation(null);
                } else if (activeCustomSegmentation) {
                  // From custom segments results, go back to predefined segments
                  setActiveCustomSegmentation(null);
                } else {
                  // From predefined segments, go to previous page in app
                  onBack();
                }
              }}
              icon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>

            {/* Create Custom Segment button - only show in predefined segments view */}
            {activeTab === 'static' && !activeCustomSegmentation && enableDynamicSegmentation && (
              <Button
                variant="outline"
                onClick={() => {
                  // Clear previous segmentation state so user can create a new one
                  setSelectedDynamicSegmentation(null);
                  setSelectedDynamicIndices([]);
                  setSelectedDynamicPersonaIds([]);
                  setPendingSegmentData(null);
                  setActiveTab('dynamic');
                }}
                className="border-[#07E5D1] text-[#07E5D1] hover:bg-[#E8FCFA]"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Create Custom Segment
                </span>
              </Button>
            )}

            {/* Change Query button - only show when viewing custom segment results */}
            {activeTab === 'static' && activeCustomSegmentation && (
              <Button
                variant="outline"
                onClick={() => {
                  // Clear all segmentation state for a fresh start
                  setActiveCustomSegmentation(null);
                  setSelectedDynamicSegmentation(null);
                  setSelectedDynamicIndices([]);
                  setSelectedDynamicPersonaIds([]);
                  setPendingSegmentData(null);
                  setActiveTab('dynamic');
                }}
                className="border-[#07E5D1] text-[#07E5D1] hover:bg-[#E8FCFA]"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Change Query
                </span>
              </Button>
            )}

            <div className="relative group">
              {(() => {
                // Check for selections based on current view
                const hasCustomSelections = activeCustomSegmentation
                  ? (customSegmentSelections[activeCustomSegmentation.id] || []).length > 0
                  : Object.values(customSegmentSelections).some((indices) => indices.length > 0);
                const hasAnySelection = activeCustomSegmentation
                  ? hasCustomSelections
                  : selectedSegments.length > 0 || hasCustomSelections;

                return (
                  <>
                    <Button
                      onClick={handleNext}
                      disabled={
                        (activeTab === 'dynamic' && isDynamicProcessing) ||
                        (activeTab === 'dynamic' &&
                          selectedDynamicSegmentation &&
                          selectedDynamicIndices.length === 0) ||
                        (activeTab === 'static' && !hasAnySelection)
                      }
                    >
                      {activeTab === 'dynamic' ? (
                        isDynamicProcessing ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Creating Segments...
                          </span>
                        ) : selectedDynamicSegmentation ? (
                          'Continue to Review'
                        ) : (
                          <span className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            Create Segments
                          </span>
                        )
                      ) : (
                        btnText
                      )}
                    </Button>
                    {/* Tooltip when button disabled due to no selection */}
                    {activeTab === 'dynamic' &&
                      selectedDynamicSegmentation &&
                      selectedDynamicIndices.length === 0 && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          Select at least one segment to continue
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      )}
                    {/* Tooltip for static view when no segments selected */}
                    {activeTab === 'static' && !hasAnySelection && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        Select at least one segment to continue
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {renderTooltip()}

      {/* Persona Viewing Modal */}
      <Dialog open={isPersonaModalOpen} onOpenChange={setIsPersonaModalOpen}>
        <DialogContent
          className="h-[80vh] w-[90vw] md:w-[85vw] max-w-[1400px] !p-0 rounded-xl overflow-hidden border border-gray-200 shadow-xl relative"
          style={{ maxWidth: '1400px', margin: 'auto' }}
        >
          <CustomDialogClose onClick={() => setIsPersonaModalOpen(false)}>
            <X className="h-5 w-5" />
          </CustomDialogClose>
          <DialogHeader className="border-b pb-4 pt-5 px-6 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center">
              <div>
                <DialogTitle className="text-xl font-semibold text-gray-800 flex items-center">
                  <User className="w-5 h-5 mr-3 text-blue-600" />
                  {selectedSegmentForPersonas &&
                    segments.find((s) => s.id === selectedSegmentForPersonas)?.name}
                </DialogTitle>
                <p className="text-sm text-gray-600 mt-1 ml-8">
                  Explore detailed profiles for this segment
                </p>
              </div>
            </div>
          </DialogHeader>

          {(() => {
            const shownCount = Object.values(rolePersonaMap).reduce(
              (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
              0,
            );
            if (previewTotal === null || previewTotal <= shownCount) return null;
            return (
              <div className="px-6 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800">
                Showing the first {shownCount} of {previewTotal} profiles for a faster preview. All{' '}
                {previewTotal} are included when you run the simulation.
              </div>
            );
          })()}

          {loadingPersonas || loadingPersonaIds ? (
            <div className="flex flex-col justify-center items-center h-full py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-600">Loading profiles...</p>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row h-full">
              {/* Left side: Role accordion with personas */}
              <div className="md:w-[340px] border-r overflow-hidden flex flex-col h-full bg-gradient-to-b from-gray-50 to-white">
                <div className="p-5 border-b">
                  <h3 className="text-sm font-medium text-gray-800 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-blue-600" />
                    Profiles by Role
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Select a role to explore detailed profiles
                  </p>
                </div>

                <div className="overflow-y-auto h-[58vh] custom-scrollbar">
                  {(() => {
                    const roles = Object.keys(rolePersonaMap);
                    const isSingleUncategorized =
                      roles.length === 1 &&
                      ['uncategorized', 'others', 'all profiles', 'all'].includes(
                        roles[0].toLowerCase(),
                      );

                    // Show loading state
                    if (loadingPersonas) {
                      return (
                        <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                          <Loader2 className="w-10 h-10 text-[#07E5D1] mb-3 animate-spin" />
                          <p className="text-sm font-medium text-gray-700">Loading profiles...</p>
                          <p className="text-xs text-gray-400 mt-1">This may take a moment</p>
                        </div>
                      );
                    }

                    if (roles.length === 0) {
                      return (
                        <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                          <Users className="w-12 h-12 text-gray-300 mb-3" />
                          <p className="text-sm">No profiles found</p>
                        </div>
                      );
                    }

                    // If single uncategorized group, show personas directly without category header
                    if (isSingleUncategorized) {
                      const allPersonas = rolePersonaMap[roles[0]] || [];
                      return (
                        <div className="py-2 px-3">
                          {allPersonas.length > 0 ? (
                            allPersonas.map((persona) => (
                              <button
                                key={persona.id}
                                onClick={() => {
                                  try {
                                    setSelectedPersona(persona);
                                  } catch (error) {
                                    console.error('Error selecting persona:', error);
                                  }
                                }}
                                className={cn(
                                  'w-full text-left px-4 py-3 my-1.5 rounded-lg hover:bg-gray-50 transition-all duration-150',
                                  selectedPersona?.id === persona.id
                                    ? 'bg-white shadow-sm border border-blue-200 ring-1 ring-blue-100'
                                    : 'border border-gray-100 bg-white/50',
                                )}
                              >
                                <div className="font-medium text-gray-800">{persona.name}</div>
                                <div className="text-xs text-gray-500 mt-1 flex items-center">
                                  <Briefcase className="w-3 h-3 mr-1.5 text-gray-400" />
                                  {persona.four_word_description ||
                                    persona.job_title ||
                                    persona.occupation ||
                                    persona.data?.job_title ||
                                    persona.category ||
                                    persona.demographics?.occupation ||
                                    persona.demographic?.occupation ||
                                    'No title'}
                                  {persona.company_name && ` • ${persona.company_name}`}
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="p-4 text-xs text-gray-500 italic text-center">
                              Loading profiles...
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Multiple categories - show with collapsible headers
                    return (
                      <div className="divide-y">
                        {Object.entries(rolePersonaMap).map(([role, personas]) => (
                          <div key={role} className="border-b">
                            <button
                              onClick={() => toggleRoleExpansion(role)}
                              className={cn(
                                'w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-all duration-200 focus:outline-none',
                                expandedRoles[role] && 'bg-blue-50/70',
                              )}
                            >
                              <div className="flex items-center">
                                {expandedRoles[role] ? (
                                  <ChevronDown className="w-4 h-4 mr-3 text-blue-600 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" />
                                )}
                                <div className="text-left">
                                  <span className="font-medium text-gray-800">{role}</span>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {personas?.length || 0}{' '}
                                    {personas?.length === 1 ? 'profile' : 'profiles'}
                                  </div>
                                </div>
                              </div>
                            </button>
                            {/* Expanded personas list */}
                            {expandedRoles[role] && (
                              <div className="bg-blue-50/40 py-2 px-4">
                                <div>
                                  {getPersonasForRole(role).length > 0 ? (
                                    getPersonasForRole(role).map((persona) => (
                                      <button
                                        key={persona.id}
                                        onClick={() => {
                                          try {
                                            setSelectedPersona(persona);
                                          } catch (error) {
                                            console.error('Error selecting persona:', error);
                                          }
                                        }}
                                        className={cn(
                                          'w-full text-left px-4 py-3 my-1.5 rounded-lg hover:bg-white transition-all duration-150 ml-4',
                                          selectedPersona?.id === persona.id
                                            ? 'bg-white shadow-sm border border-blue-200 ring-1 ring-blue-100'
                                            : 'border border-transparent',
                                        )}
                                      >
                                        <div className="font-medium text-gray-800">
                                          {persona.name}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1 flex items-center">
                                          <Briefcase className="w-3 h-3 mr-1.5 text-gray-400" />
                                          {persona.four_word_description ||
                                            persona.job_title ||
                                            persona.occupation ||
                                            persona.data?.job_title ||
                                            persona.category ||
                                            persona.demographics?.occupation ||
                                            persona.demographic?.occupation ||
                                            'No title'}
                                          {persona.company_name && ` • ${persona.company_name}`}
                                        </div>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="p-4 text-xs text-gray-500 italic text-center">
                                      Loading profiles...
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Right side: Persona Details */}
              <div className="flex-1 overflow-hidden flex flex-col h-[69vh]">
                <div className="overflow-y-auto p-6 bg-gradient-to-br from-gray-50/70 to-white h-full custom-scrollbar">
                  {selectedPersona ? (
                    <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-fadeIn max-w-4xl mx-auto">
                      {/* Persona Header */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-200">
                        <h3 className="text-2xl font-semibold text-gray-800 flex items-center">
                          <span className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 border border-blue-200">
                            {selectedPersona.name.charAt(0)}
                          </span>
                          {selectedPersona.name}
                        </h3>
                        <div className="flex items-center text-sm text-gray-600 mt-3 ml-1">
                          <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
                          <span className="font-medium">
                            {selectedPersona.four_word_description ||
                              selectedPersona.data?.job_title ||
                              selectedPersona.demographics?.occupation ||
                              selectedPersona.job_title ||
                              selectedPersona.demographic?.occupation ||
                              'No title available'}
                            {selectedPersona.company_name && ` at ${selectedPersona.company_name}`}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-6">
                        {/* Dynamic rendering of all persona fields */}
                        {renderAllPersonaFields(selectedPersona)}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <div className="bg-blue-50 p-6 rounded-full mb-4 border border-blue-100">
                        <User className="w-12 h-12 text-blue-400" />
                      </div>
                      <p className="text-lg text-gray-600 mb-2">Select a profile</p>
                      <p className="text-sm text-gray-500">
                        Choose from the list on the left to view detailed information
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </StepContainer>
  );
};

export default UnifiedSegmentsSelector;
